import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Checkbox } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import type { ServiceResa } from '../../lib/reservationsApi';
import type { FermetureJour, NouvelleFermeture } from '../../lib/reservationsReglagesApi';
import { SelecteurPlage } from './SelecteurPlage';
import {
    CASES_SOBRES, TAILLES_DIX_ET_PLUS, TAILLES_SIMPLES, creneauxDuService, phraseConfirmation, phraseFermeture, plageParDefaut, resoudrePlage, type PlageChoisie,
} from './fermetures';

/**
 * Bouton « ⦸ » du sélecteur de service (maquette L06-09, « Afficher complet »). On peut COCHER PLUSIEURS TYPES dans la même fenêtre — « Tout le
 * service », « Certains créneaux » (à partir de, entre deux heures, ou cases) et « Un nombre de couverts » (tailles 1 à 10 et plus, avec leur
 * portée) — et « Ajouter une autre fermeture » en met une de côté avant d'en préparer une autre. « Enregistrer » applique tout d'un coup
 * (route /fermetures/lot : tout ou rien). Chaque fermeture vaut pour le jour affiché et reste active jusqu'à sa réouverture ; elles se
 * combinent, la plus large l'emporte ; la saisie à la main reste toujours possible. Service déjà complet : la fenêtre propose « Rouvrir ».
 */
interface Props {
    service: ServiceResa | null;
    dateLibelle: string;
    /** Fermeture « service entier » déjà posée : la fenêtre devient « Rouvrir : … ». */
    serviceFerme?: FermetureJour;
    codeProtege: boolean;
    onClose: () => void;
    /** Pose les fermetures d'un seul bloc. Rejette l'erreur : la fenêtre la montre. */
    onFermer: (f: NouvelleFermeture[]) => Promise<void>;
    onRouvrir: (f: FermetureJour) => Promise<void>;
}

type Type = 'service' | 'creneaux' | 'couverts';
const TYPES: { v: Type; titre: string; aide: string }[] = [
    { v: 'service', titre: 'Tout le service', aide: 'Plus aucune réservation en ligne sur ce service, pour ce jour.' },
    { v: 'creneaux', titre: 'Certains créneaux', aide: 'À partir d’une heure, entre deux heures, ou en cochant les créneaux.' },
    { v: 'couverts', titre: 'Un nombre de couverts', aide: 'Vous fermez une ou plusieurs tailles de groupe (par exemple les groupes de 4), sans fermer le créneau.' },
];

export const FermerService: React.FC<Props> = ({ service, dateLibelle, serviceFerme, codeProtege, onClose, onFermer, onRouvrir }) => {
    const [coches, setCoches] = useState<Record<Type, boolean>>({ service: true, creneaux: false, couverts: false });
    const [plageCreneaux, setPlageCreneaux] = useState<PlageChoisie>(plageParDefaut([], 'apartir'));
    const [tailles, setTailles] = useState<number[]>([]);
    const [plageCouverts, setPlageCouverts] = useState<PlageChoisie>(plageParDefaut([], 'service'));
    const [mises, setMises] = useState<NouvelleFermeture[]>([]);
    const [erreur, setErreur] = useState('');
    const [occupe, setOccupe] = useState(false);

    const creneaux = useMemo(() => (service ? creneauxDuService({ heure_debut: service.heure_debut, heure_fin: service.heure_fin, pas_minutes: service.pas_minutes }) : []), [service]);
    const pas = service?.pas_minutes || 15;
    useEffect(() => {
        if (!service) return;
        setCoches({ service: true, creneaux: false, couverts: false }); setTailles([]); setMises([]); setErreur(''); setOccupe(false);
        setPlageCreneaux(plageParDefaut(creneaux, 'apartir')); setPlageCouverts(plageParDefaut(creneaux, 'service'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service?.id]);

    const basculer = <T,>(l: T[], x: T) => (l.includes(x) ? l.filter((y) => y !== x) : [...l, x]);
    const dixEtPlus = TAILLES_DIX_ET_PLUS.every((x) => tailles.includes(x));
    const basculerDix = () => setTailles(dixEtPlus ? tailles.filter((x) => x < 10) : [...new Set([...tailles, ...TAILLES_DIX_ET_PLUS])]);

    // Ce que la sélection COURANTE produit (une ligne par plage), et si elle est complète.
    const courant = useMemo(() => {
        const items: NouvelleFermeture[] = []; let valide = true; let nb = 0;
        if (!service) return { items, valide: false, nb };
        if (coches.service) { items.push({ type: 'service', resa_service_id: service.id }); nb++; }
        if (coches.creneaux) {
            nb++; const p = resoudrePlage(plageCreneaux, creneaux, pas, service.heure_fin) || [];
            if (p.length === 0) valide = false; else p.forEach((x) => items.push({ type: 'creneaux', resa_service_id: service.id, ...x }));
        }
        if (coches.couverts) {
            nb++;
            const p = resoudrePlage(plageCouverts, creneaux, pas, service.heure_fin);
            if (tailles.length === 0 || (p !== null && p.length === 0)) valide = false;
            else if (p === null) items.push({ type: 'couverts', resa_service_id: service.id, tailles });
            else p.forEach((x) => items.push({ type: 'couverts', resa_service_id: service.id, tailles, ...x }));
        }
        return { items, valide: valide && nb > 0, nb };
    }, [service, coches, plageCreneaux, plageCouverts, tailles, creneaux, pas]);

    const aEnregistrer = [...mises, ...(courant.valide ? courant.items : [])];
    const sv = service ? [service] : [];
    // Le bouton nomme l'effet : une seule fermeture → sa phrase exacte ; plusieurs → leur nombre (la liste ci-dessus les détaille).
    const phrase = aEnregistrer.length === 0 ? 'Choisissez quoi fermer'
        : aEnregistrer.length === 1 ? phraseConfirmation(aEnregistrer[0], sv)
            : `Enregistrer les ${aEnregistrer.length} fermetures`;
    const autre = () => { if (!courant.valide) return; setMises((l) => [...l, ...courant.items]); setCoches({ service: false, creneaux: false, couverts: false }); setTailles([]); setErreur(''); };

    const lancer = async (fn: () => Promise<void>) => { setOccupe(true); setErreur(''); try { await fn(); } catch (e) { setErreur(e instanceof Error ? e.message : 'Action impossible.'); setOccupe(false); } };
    const nomSvc = service?.nom || '';
    const rouvrir = !!serviceFerme;

    return (
        <Modal open={!!service} onClose={onClose} widthPx={540} title={service ? `${rouvrir ? 'Rouvrir' : 'Afficher complet'} : ${nomSvc}` : ''} subtitle={`Pour ${dateLibelle}. Reste actif jusqu'à ce que vous le rouvriez.`}
            footer={(
                <div className="flex w-full flex-wrap items-center justify-end gap-2">
                    <button type="button" className={`${pillSecondary(36)} max-lg:!h-11`} onClick={onClose}>Annuler</button>
                    {rouvrir
                        ? <button type="button" className={`${pillPrimary(36)} max-lg:!h-11`} disabled={occupe} onClick={() => void lancer(() => onRouvrir(serviceFerme!))}>Rouvrir le service du {nomSvc.toLowerCase()}</button>
                        : <button type="button" className={`${pillPrimary(36)} max-lg:!h-11`} disabled={occupe || aEnregistrer.length === 0} onClick={() => void lancer(() => onFermer(aEnregistrer))}>{phrase}</button>}
                </div>
            )}>
            {rouvrir ? (
                <p className="text-[13px] text-content">Le service du {nomSvc.toLowerCase()} est fermé pour ce jour : plus aucune réservation en ligne. Vous pouvez toujours saisir des réservations à la main. Les autres fermetures posées (créneaux, tailles de groupe) sont conservées et reprennent à la réouverture.</p>
            ) : (
                <div className="space-y-4">
                    <p className="text-[12.5px] text-content-muted">Cochez un ou plusieurs types de fermeture : ils s'enregistrent ensemble.</p>
                    <fieldset className="space-y-2.5">
                        <legend className="sr-only">Ce que vous fermez</legend>
                        {TYPES.map((o) => (
                            <div key={o.v} className={`rounded-xl border px-3.5 py-3 space-y-3 ${coches[o.v] ? 'border-content bg-surface-soft' : 'border-border'}`}>
                                <label className="flex items-start gap-3 cursor-pointer max-lg:min-h-[44px]">
                                    <input type="checkbox" className="mt-1" checked={coches[o.v]} onChange={() => setCoches((c) => ({ ...c, [o.v]: !c[o.v] }))} />
                                    <span><span className="block text-[13.5px] font-semibold text-content">{o.titre}</span><span className="block text-[12.5px] text-content-muted">{o.aide}</span></span>
                                </label>
                                {o.v === 'creneaux' && coches.creneaux && <SelecteurPlage nom="créneaux" creneaux={creneaux} finService={service?.heure_fin || ''} value={plageCreneaux} onChange={setPlageCreneaux} />}
                                {o.v === 'couverts' && coches.couverts && (
                                    <div className="space-y-3">
                                        <div role="group" aria-label="Tailles de groupe" className={`${CASES_SOBRES} grid grid-cols-5 max-sm:grid-cols-4 gap-2`}>
                                            {TAILLES_SIMPLES.map((n) => <Checkbox key={n} label={String(n)} checked={tailles.includes(n)} onChange={() => setTailles(basculer(tailles, n))} className="max-lg:min-h-[44px]" />)}
                                            <Checkbox label="10 et +" checked={dixEtPlus} onChange={basculerDix} className="max-lg:min-h-[44px]" />
                                        </div>
                                        <p className="text-[12px] font-semibold text-content">Valable pour</p>
                                        <SelecteurPlage nom="tailles de groupe" avecService creneaux={creneaux} finService={service?.heure_fin || ''} value={plageCouverts} onChange={setPlageCouverts} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </fieldset>

                    {coches.service && (coches.creneaux || coches.couverts || mises.length > 0) && <p className="text-[12.5px] text-content-muted">Le service entier fermé l'emporte : les autres fermetures sont conservées et reprendront si vous rouvrez le service.</p>}

                    <button type="button" className={`${pillSecondary(34)} max-lg:!h-11`} disabled={!courant.valide} onClick={autre}>Ajouter une autre fermeture</button>

                    {aEnregistrer.length > 0 && (
                        <section aria-label="Fermetures à enregistrer" className="rounded-xl border border-border px-3.5 py-3 space-y-1.5">
                            <h3 className="text-[12.5px] font-semibold text-content">À enregistrer ({aEnregistrer.length})</h3>
                            <ul className="space-y-1.5">
                                {mises.map((m, i) => (
                                    <li key={`m${i}`} className="flex flex-wrap items-center justify-between gap-2 text-[13px] text-content">
                                        <span>{phraseFermeture(m, sv)}</span>
                                        <button type="button" className="text-[12.5px] font-semibold underline min-h-[28px] max-lg:min-h-[44px]" onClick={() => setMises((l) => l.filter((_, k) => k !== i))}>Retirer</button>
                                    </li>
                                ))}
                                {courant.valide && courant.items.map((m, i) => <li key={`c${i}`} className="text-[13px] text-content">{phraseFermeture(m, sv)}</li>)}
                            </ul>
                        </section>
                    )}
                    <p className="text-[12px] text-content-muted">Effet immédiat sur la page de réservation. Vous pouvez toujours saisir des réservations à la main.{codeProtege ? ' Le code manager est demandé.' : ''}</p>
                </div>
            )}
            {erreur && <div role="alert" className="mt-3 rounded-xl bg-state-danger-soft text-state-danger px-4 py-2.5 text-[13px]">{erreur}</div>}
        </Modal>
    );
};
